import type { ItemList, ListItem, WithContext } from "schema-dts";

import type { VideoPlayerContainerProps } from "@jti/ui/components/VideoContainer/VideoContainer";

import type { PublisherData } from "@/components/VideoPlayer/_internal/buildVideoObjectData";
import { buildVideoObjectData } from "@/components/VideoPlayer/_internal/buildVideoObjectData";

type VideoContainerItem = VideoPlayerContainerProps & {
  jsonLdUploadDate?: string;
  jsonLdDuration?: string;
};

type VideoContainerJsonLDProps = {
  title: string;
  items: VideoContainerItem[];
  publisher?: PublisherData;
};

function getContentUrl(item: VideoContainerItem): string | undefined {
  if (item.videoType === "youtube") {
    return item.youtubeConfiguration?.externalLinkVideo;
  }

  return (
    item.hostedVideoConfiguration?.desktopSrc ||
    item.hostedVideoConfiguration?.tabletSrc ||
    item.hostedVideoConfiguration?.mobileSrc
  );
}

function buildItemListJsonLd(
  { title, items }: VideoContainerJsonLDProps,
  publisher: PublisherData | undefined
): WithContext<ItemList> | null {
  const listItems = items
    .map((item, index): ListItem | null => {
      const videoObject = buildVideoObjectData({
        name: item.title,
        description: item.description,
        thumbnailUrl: item.posterImage,
        thumbnailAlt: item.posterAlt,
        contentUrl: getContentUrl(item),
        uploadDate: item.jsonLdUploadDate,
        duration: item.jsonLdDuration,
        publisher,
      });

      if (!videoObject) {
        return null;
      }

      return {
        "@type": "ListItem",
        position: index + 1,
        item: videoObject,
      };
    })
    .filter((item): item is ListItem => item !== null);

  if (listItems.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: title,
    itemListElement: listItems,
  };
}

export function VideoContainerJsonLD({ title, items, publisher }: VideoContainerJsonLDProps) {
  const jsonLd = buildItemListJsonLd({ title, items }, publisher);

  if (!jsonLd) {
    return null;
  }

  return <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>;
}
